"use client";

import Image from "next/image";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

type InterestItem = {
  title: string;
  description: string;
  icon?: string;
  imageURL: string;
};

export default function InterestsSection() {
  const { t } = useTranslation();
  const interests = t("interests.items", {
    returnObjects: true,
  }) as InterestItem[];

  return (
    <section id="interests" className="space-y-5">
      <div className="rounded-xl border border-black/20 bg-white p-6 dark:border-white/25 dark:bg-neutral-950">
        <p className="font-display mb-2 text-sm uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-400">
          Outside Work
        </p>
        <h2 className="font-display text-3xl tracking-[-0.04em] md:text-5xl">
          {t("interests.title")}
        </h2>
        <p className="mt-3 max-w-2xl text-sm text-neutral-600 md:text-base dark:text-neutral-300">
          {t("interests.subtitle")}
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {interests.map((interest, index) => (
          <motion.article
            key={interest.title}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.45, delay: index * 0.06 }}
            className="group relative overflow-hidden rounded-xl border border-black/20 bg-white p-3 dark:border-white/25 dark:bg-neutral-950"
          >
            <div className="grid min-h-64 grid-cols-1 overflow-hidden rounded-lg border border-black/20 md:grid-cols-[1.1fr_0.9fr] dark:border-white/25">
              <div className="p-4 md:p-5">
                <p className="text-xl">{interest.icon}</p>
                <h3 className="font-display mt-2 text-2xl tracking-[-0.03em]">
                  {interest.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-neutral-600 dark:text-neutral-300">
                  {interest.description}
                </p>
              </div>

              <div className="relative h-56 md:h-full">
                <Image
                  src={interest.imageURL}
                  alt={interest.title}
                  fill
                  className="object-cover transition duration-500 group-hover:scale-105"
                />
              </div>
            </div>
          </motion.article>
        ))}
      </div>
    </section>
  );
}
